import { EnumButtonName } from '@calm/evexia-analytics';
import { useGuideQueue } from '@rhg/hooks/use-guide-queue';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    IconButton,
    IconButtonProps,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { Shuffle } from '@rhg/libs/material/icons';
import { LoopMode } from '@rhg/providers/guide-queue/types';
import { useIntl } from 'react-intl';

export interface ShuffleButtonProps {
    sx?: SxProps<Theme>;
    size?: IconButtonProps['size'];
    variant?: IconButtonProps['variant'];
    /**
     * Render a visible but non-interactive, disabled shuffle icon even when the
     * content doesn't support shuffle. Balances the transport row when a loop
     * control is shown but shuffle isn't (single loopable track) — the Figma
     * calls for a dimmed shuffle glyph in that slot, not an empty gap
     * (EVE-13089).
     */
    disabled?: boolean;
}

// Active indicator dot drawn below the glyph — same approach as LoopButton.
const styleIconWrapper = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
};

const styleActiveDot = {
    position: 'absolute',
    bottom: '-0.2em',
    width: '0.2em',
    height: '0.2em',
    borderRadius: '50%',
    bgcolor: 'info.main',
};

export function ShuffleButton({
    sx,
    size,
    variant,
    disabled = false,
}: ShuffleButtonProps) {
    const { formatMessage } = useIntl();
    const { isShuffled, isShuffleSupported, loopMode, toggleShuffle } =
        useGuideQueue();

    // Balance slot (EVE-13089): the content can't shuffle but a loop control is
    // shown, so render a dimmed, non-interactive shuffle glyph to keep the
    // transport row centered. Not a toggle — no onClick, no aria-pressed.
    if (disabled) {
        return (
            <IconButton
                skipTracking
                aria-label={formatMessage(playerMessages['player.shuffle_off'])}
                disabled
                size={size}
                variant={variant}
                sx={sx}
            >
                <Box sx={styleIconWrapper}>
                    <Shuffle color="inherit" />
                </Box>
            </IconButton>
        );
    }

    if (!isShuffleSupported) {
        return null;
    }

    // EVE-11931: shuffle and Loop-One are mutually exclusive, so the
    // control is disabled while Loop-One is active.
    const isDisabled = loopMode === LoopMode.One;

    return (
        <IconButton
            trackButton={EnumButtonName.Shuffle}
            trackProps={{
                Shuffle: isShuffled ? 'Off' : 'On',
                // Override the ambient (pre-click) IsShuffled so it matches
                IsShuffled: !isShuffled,
            }}
            aria-pressed={isShuffled}
            aria-label={formatMessage(
                playerMessages[
                    isShuffled ? 'player.shuffle_on' : 'player.shuffle_off'
                ]
            )}
            onClick={toggleShuffle}
            disabled={isDisabled}
            size={size}
            variant={variant}
            sx={sx}
        >
            <Box sx={styleIconWrapper}>
                <Shuffle color={isShuffled ? 'info' : 'inherit'} />
                {isShuffled && (
                    <Box
                        data-testid="shuffle-dot"
                        aria-hidden="true"
                        sx={styleActiveDot}
                    />
                )}
            </Box>
        </IconButton>
    );
}
